import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  AuthService,
  ENDPOINTS,
  FilteredTablePayload,
  PusherService,
  REALTIME_CHANNELS,
  TenantService,
} from '@kuiper/ibusiness-core';
import {
  BackgroundTask,
  BackgroundTaskProgress,
  BackgroundTaskLogsQuery,
  BackgroundTaskLogsResponse,
  BackgroundTasksResponse,
} from './background-task.model';

@Injectable({
  providedIn: 'root',
})
export class BackgroundTaskService {
  private readonly http = inject(HttpClient);
  private readonly pusherService = inject(PusherService);
  private readonly authService = inject(AuthService);
  private readonly tenantService = inject(TenantService);
  private readonly endpoints = ENDPOINTS.Application.BackgroundTasks;

  /**
   * The user's tasks as they progress, from the socket. Emits `null` on every join of the channel (the first one
   * and after a dropped connection): events sent before it were missed, so the caller reloads. Unsubscribing
   * leaves the channel.
   */
  progress(): Observable<BackgroundTaskProgress | null> {
    return new Observable<BackgroundTaskProgress | null>(subscriber => {
      const user = this.authService.centralUser() || this.authService.user();
      const channelName = REALTIME_CHANNELS.BACKGROUND_TASKS.replace(
        '{tenant}',
        this.tenantService.getTenant()
      ).replace('{userId}', `${user?.id}`);
      const channel = this.pusherService.subscribe(channelName);

      // Every join, the first one too: a short import can finish while the page is still joining.
      channel.bind('pusher:subscription_succeeded', () =>
        subscriber.next(null)
      );
      channel.bind(
        'background_task.progressed',
        (event: BackgroundTaskProgress) => subscriber.next(event)
      );

      return () => this.pusherService.unsubscribe(channelName);
    });
  }

  list(
    page: number,
    perPage: number,
    states: string[] = [],
    sort?: FilteredTablePayload['sortColumn']
  ): Observable<BackgroundTasksResponse> {
    let params = new HttpParams()
      .append('page', page)
      .append('per_page', perPage);

    for (const state of states) {
      params = params.append('filters[states][]', state);
    }

    if (sort?.key) {
      params = params.append(`sort[${sort.key}][type]`, sort.value);
    }

    return this.http.get<BackgroundTasksResponse>(this.endpoints.List, {
      params,
    });
  }

  show(id: number): Observable<BackgroundTask> {
    return this.http
      .get<{ data: BackgroundTask }>(this.url(this.endpoints.Show, id))
      .pipe(map(response => response.data));
  }

  logs(
    id: number,
    query: BackgroundTaskLogsQuery
  ): Observable<BackgroundTaskLogsResponse> {
    const optional: Record<string, string | undefined> = {
      level: query.level,
      'filters[keyword]': query.keyword,
      'filters[budget_type]': query.budgetType,
    };
    let params = new HttpParams()
      .append('page', query.page)
      .append('per_page', query.perPage);

    Object.entries(optional)
      .filter(([, value]) => !!value)
      .forEach(
        ([key, value]) => (params = params.append(key, value as string))
      );

    return this.http.get<BackgroundTaskLogsResponse>(
      this.url(this.endpoints.Logs, id),
      { params }
    );
  }

  private url(template: string, id: number): string {
    return template.replace('{id}', id.toString());
  }
}
